import type { CSSProperties, JSX } from 'react';
import type { PageMedia } from '../types.js';

interface MediaBlockProps {
  media: PageMedia;
  radius?: number;
  maxHeight?: number;
  fit?: 'cover' | 'contain';
}

export function MediaBlock({
  media,
  radius = 12,
  maxHeight = 240,
  fit = 'cover',
}: MediaBlockProps): JSX.Element | null {
  if (!media?.url) return null;
  const type = media.type ?? inferType(media.url);
  const style: CSSProperties = {
    width: '100%',
    maxHeight,
    borderRadius: radius,
    objectFit: fit,
    display: 'block',
  };

  if (type === 'video') return <VideoMedia media={media} style={style} />;
  if (type === 'iframe') return <IframeMedia media={media} radius={radius} maxHeight={maxHeight} />;
  return <ImageMedia media={media} style={style} />;
}

function ImageMedia({ media, style }: { media: PageMedia; style: CSSProperties }): JSX.Element {
  return <img src={media.url!} alt={media.alt ?? ''} style={style} />;
}

function VideoMedia({ media, style }: { media: PageMedia; style: CSSProperties }): JSX.Element {
  return (
    <video
      src={media.url}
      controls
      playsInline
      style={style}
      aria-label={media.alt ?? 'Survey video'}
    />
  );
}

function IframeMedia({
  media,
  radius,
  maxHeight,
}: {
  media: PageMedia;
  radius: number;
  maxHeight: number;
}): JSX.Element {
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        maxHeight,
        borderRadius: radius,
        overflow: 'hidden',
        background: '#000',
      }}
    >
      <iframe
        src={media.url}
        title={media.alt ?? 'Survey embed'}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
      />
    </div>
  );
}

function inferType(url: string): 'image' | 'video' | 'iframe' {
  const lower = url.toLowerCase();
  if (/\.(mp4|webm|mov|ogv)$/i.test(lower)) return 'video';
  if (/(youtube\.com|youtu\.be|vimeo\.com|loom\.com)/i.test(lower)) return 'iframe';
  return 'image';
}
